/**
 * Permissions API Service
 * Handles all API calls related to user permissions, profiles, and manager assignments.
 *
 * IMPORTANT: Use the same API base URL and auth handling as the rest of the app
 * (see src/services/api.js). This ensures requests work both in local dev and
 * behind nginx in production (where /api is reverse-proxied).
 */

import { apiClient as api } from './api';

// ===== USER PERMISSIONS =====

export const getUserPermissionsOverview = async (userId) => {
  try {
    const response = await api.get(`/permissions/user/${userId}/overview`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to fetch user permissions overview';
  }
};

export const getUserPermissions = async (userId) => {
  try {
    const response = await api.get(`/permissions/user/${userId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to fetch user permissions';
  }
};

export const createUserPermission = async (userId, permission) => {
  try {
    const response = await api.post(`/permissions/user/${userId}/permission`, permission);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to create permission';
  }
};

export const updateUserPermission = async (userId, module, update) => {
  try {
    const response = await api.put(`/permissions/user/${userId}/permission/${module}`, update);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to update permission';
  }
};

export const deleteUserPermission = async (userId, module) => {
  try {
    await api.delete(`/permissions/user/${userId}/permission/${module}`);
    return { success: true };
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to delete permission';
  }
};

export const batchUpdateUserPermissions = async (userId, permissions) => {
  try {
    const response = await api.post(`/permissions/user/${userId}/batch-update`, {
      user_id: userId,
      permissions
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to batch update permissions';
  }
};

// ===== PERMISSION PROFILES =====

export const listPermissionProfiles = async (skip = 0, limit = 10) => {
  try {
    const response = await api.get(`/permissions/profiles`, {
      params: { skip, limit }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to fetch permission profiles';
  }
};

export const getPermissionProfile = async (profileId) => {
  try {
    const response = await api.get(`/permissions/profiles/${profileId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to fetch permission profile';
  }
};

export const createPermissionProfile = async (profile) => {
  try {
    const response = await api.post(`/permissions/profiles`, profile);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to create permission profile';
  }
};

export const updatePermissionProfile = async (profileId, update) => {
  try {
    const response = await api.put(`/permissions/profiles/${profileId}`, update);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to update permission profile';
  }
};

export const deletePermissionProfile = async (profileId) => {
  try {
    await api.delete(`/permissions/profiles/${profileId}`);
    return { success: true };
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to delete permission profile';
  }
};

export const applyProfileToUser = async (userId, profileId) => {
  try {
    const response = await api.post(
      `/permissions/user/${userId}/apply-profile/${profileId}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to apply profile to user';
  }
};

// ===== MANAGER COMPANY ASSIGNMENTS =====

export const getManagerCompanies = async (managerId) => {
  try {
    const response = await api.get(`/permissions/managers/${managerId}/companies`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to fetch manager companies';
  }
};

export const assignCompanyToManager = async (managerId, companyId) => {
  try {
    const response = await api.post(`/permissions/managers/company`, {
      manager_id: managerId,
      company_id: companyId
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to assign company to manager';
  }
};

export const bulkAssignCompaniesToManager = async (managerId, companyIds) => {
  try {
    const response = await api.post(`/permissions/managers/bulk-assign`, {
      manager_id: managerId,
      company_ids: companyIds
    });
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to bulk assign companies';
  }
};

export const removeCompanyFromManager = async (managerId, companyId) => {
  try {
    await api.delete(`/permissions/managers/${managerId}/company/${companyId}`);
    return { success: true };
  } catch (error) {
    throw error.response?.data?.detail || 'Failed to remove company from manager';
  }
};
